import { REFRESH_BENEFITS_TOKEN } from "./constants";
import { clearGuestId } from "./guestSession";

const LEGACY_BENEFITS_ACCESS_TOKEN_KEY = "ncludeBenefitsAccessToken";
const BENEFITS_ACCESS_EXPIRY_KEY = "ncludeBenefitsAccessExpiresAt";
const BENEFITS_REFRESH_TOKEN_KEY = "ncludeBenefitsRefreshToken";
const BENEFITS_REFRESH_EXPIRY_KEY = "ncludeBenefitsRefreshExpiresAt";
const BENEFITS_GUEST_SESSION_KEY = "ncludeBenefitsGuestSession";
const LEGACY_BENEFITS_CONVERSATION_ID_KEY = "ncludeBenefitsConversationId";
const BENEFITS_CONVERSATION_ID_KEY = "ncludeBenefitsActiveConversationId";
const BENEFITS_DRAFT_IDS_KEY = "ncludeBenefitsDraftConversationIds";

type BenefitsSessionDetails = {
  token: string;
  refreshToken: string;
  expiresInSeconds?: number;
  refreshExpiresInSeconds?: number;
};

type BenefitsRefreshResponse = {
  success?: boolean;
  message?: string;
  accessToken?: string;
  expiresInSeconds?: number;
  refreshToken?: string;
  refreshExpiresInSeconds?: number;
};

const DEFAULT_ACCESS_SECONDS = 60 * 60;
const DEFAULT_REFRESH_SECONDS = 30 * 24 * 60 * 60;
let accessTokenInMemory: string | null = null;
let refreshRequest: Promise<string | null> | null = null;

const getExpiry = (duration: number | undefined, fallback: number) => {
  const seconds = Number(duration);
  return Date.now() + (Number.isFinite(seconds) && seconds > 0 ? seconds : fallback) * 1000;
};

const replaceBenefitsTokens = ({
  token,
  refreshToken,
  expiresInSeconds,
  refreshExpiresInSeconds,
}: BenefitsSessionDetails) => {
  accessTokenInMemory = token;
  sessionStorage.setItem(BENEFITS_ACCESS_EXPIRY_KEY, String(getExpiry(expiresInSeconds, DEFAULT_ACCESS_SECONDS)));
  localStorage.setItem(BENEFITS_REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(
    BENEFITS_REFRESH_EXPIRY_KEY,
    String(getExpiry(refreshExpiresInSeconds, DEFAULT_REFRESH_SECONDS))
  );
  sessionStorage.removeItem(LEGACY_BENEFITS_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(BENEFITS_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(BENEFITS_REFRESH_EXPIRY_KEY);
};

export const saveBenefitsSession = (details: BenefitsSessionDetails) => {
  localStorage.removeItem(BENEFITS_GUEST_SESSION_KEY);
  replaceBenefitsTokens(details);
};

export const startBenefitsGuestSession = () => {
  localStorage.setItem(BENEFITS_GUEST_SESSION_KEY, "true");
};

export const isBenefitsGuestSession = () =>
  localStorage.getItem(BENEFITS_GUEST_SESSION_KEY) === "true";

export const clearBenefitsSession = () => {
  accessTokenInMemory = null;
  sessionStorage.removeItem(LEGACY_BENEFITS_ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(LEGACY_BENEFITS_CONVERSATION_ID_KEY);
  sessionStorage.removeItem("ncludeBenefitsEmail");
  sessionStorage.removeItem(BENEFITS_ACCESS_EXPIRY_KEY);
  sessionStorage.removeItem(BENEFITS_REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(BENEFITS_REFRESH_EXPIRY_KEY);
  localStorage.removeItem(LEGACY_BENEFITS_CONVERSATION_ID_KEY);
  localStorage.removeItem(BENEFITS_CONVERSATION_ID_KEY);
  localStorage.removeItem(BENEFITS_DRAFT_IDS_KEY);
  localStorage.removeItem("ncludeBenefitsEmail");
  localStorage.removeItem(BENEFITS_REFRESH_TOKEN_KEY);
  localStorage.removeItem(BENEFITS_REFRESH_EXPIRY_KEY);
  localStorage.removeItem(BENEFITS_GUEST_SESSION_KEY);
  clearGuestId();
};

export const getBenefitsAccessToken = () => {
  const expiresAt = Number(sessionStorage.getItem(BENEFITS_ACCESS_EXPIRY_KEY));

  if (!accessTokenInMemory || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    accessTokenInMemory = null;
    return null;
  }

  return accessTokenInMemory;
};

const getBenefitsRefreshToken = () => {
  const storedSessionToken = sessionStorage.getItem(BENEFITS_REFRESH_TOKEN_KEY);
  const storedSessionExpiry = sessionStorage.getItem(BENEFITS_REFRESH_EXPIRY_KEY);

  // Preserve sessions created by the previous session-only frontend release.
  if (storedSessionToken && storedSessionExpiry) {
    localStorage.setItem(BENEFITS_REFRESH_TOKEN_KEY, storedSessionToken);
    localStorage.setItem(BENEFITS_REFRESH_EXPIRY_KEY, storedSessionExpiry);
    sessionStorage.removeItem(BENEFITS_REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(BENEFITS_REFRESH_EXPIRY_KEY);
  }

  const refreshToken = localStorage.getItem(BENEFITS_REFRESH_TOKEN_KEY);
  const expiresAt = Number(localStorage.getItem(BENEFITS_REFRESH_EXPIRY_KEY));

  if (!refreshToken || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
    clearBenefitsSession();
    return null;
  }

  return refreshToken;
};

export const refreshBenefitsAccessToken = () => {
  if (refreshRequest) return refreshRequest;

  const currentRefreshToken = getBenefitsRefreshToken();
  if (!currentRefreshToken) return Promise.resolve(null);

  refreshRequest = (async () => {
    try {
      const response = await fetch(REFRESH_BENEFITS_TOKEN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      if (!response.ok) throw new Error(`Benefits refresh failed with status ${response.status}`);

      const result = (await response.json()) as BenefitsRefreshResponse;
      if (result.success === false || !result.accessToken || !result.refreshToken) {
        throw new Error(result.message || "The Benefits session could not be renewed.");
      }

      replaceBenefitsTokens({
        token: result.accessToken,
        refreshToken: result.refreshToken,
        expiresInSeconds: result.expiresInSeconds,
        refreshExpiresInSeconds: result.refreshExpiresInSeconds,
      });
      return result.accessToken;
    } catch {
      clearBenefitsSession();
      return null;
    } finally {
      refreshRequest = null;
    }
  })();

  return refreshRequest;
};

export const hasBenefitsSession = () =>
  Boolean(isBenefitsGuestSession() || getBenefitsAccessToken() || getBenefitsRefreshToken());

export const isBenefitsConversationId = (value: string | null | undefined): value is string =>
  Boolean(
    value &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
  );

const readDraftIds = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(BENEFITS_DRAFT_IDS_KEY) || "[]");
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => isBenefitsConversationId(value))
      : [];
  } catch {
    return [];
  }
};

const writeDraftIds = (draftIds: string[]) => {
  localStorage.setItem(BENEFITS_DRAFT_IDS_KEY, JSON.stringify(Array.from(new Set(draftIds))));
};

export const rememberBenefitsConversationId = (conversationId: string) => {
  if (!isBenefitsConversationId(conversationId)) return;
  localStorage.setItem(BENEFITS_CONVERSATION_ID_KEY, conversationId);
};

export const isBenefitsConversationDraft = (conversationId: string) =>
  readDraftIds().includes(conversationId);

export const markBenefitsConversationPersisted = (conversationId: string) => {
  writeDraftIds(readDraftIds().filter((draftId) => draftId !== conversationId));
};

export const createBenefitsConversationId = () => {
  const conversationId = crypto.randomUUID();
  rememberBenefitsConversationId(conversationId);
  writeDraftIds([...readDraftIds(), conversationId]);
  return conversationId;
};

export const getBenefitsConversationId = () => {
  const storedId = localStorage.getItem(BENEFITS_CONVERSATION_ID_KEY);
  if (isBenefitsConversationId(storedId)) return storedId;

  // Carry forward the valid ID generated by the earlier session-only client.
  const legacyId = sessionStorage.getItem(LEGACY_BENEFITS_CONVERSATION_ID_KEY);
  sessionStorage.removeItem(LEGACY_BENEFITS_CONVERSATION_ID_KEY);
  if (isBenefitsConversationId(legacyId)) {
    rememberBenefitsConversationId(legacyId);
    return legacyId;
  }

  return createBenefitsConversationId();
};
